import { useIsMutating, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';

/**
 * Every mutation in this file is consumed by a Settings section that pipes
 * errors into the page-level banner via `onError={onError}` props. We mark
 * each mutation `meta: { silentError: true }` so the global toast pipe in
 * `lib/query-client.ts` doesn't double-display the same error.
 */
const SILENT = { silentError: true } as const;
import {
  getProfile,
  getWarmupFilterId,
  updateProfile,
  uploadAvatar,
  type ProfileDto,
  type UpdateProfileInput,
} from '@/lib/profile';
import {
  getOrganization,
  updateOrganizationTimezone,
  getWorkspaces,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  listMembers,
  listPendingInvites,
  inviteMember,
  resendInvite,
  revokeInvite,
  removeMember,
  updateMemberRole,
  addMemberToWorkspace,
  createShareableLink,
  listShareableLinks,
  revokeShareableLink,
  getQuotas,
  updateQuota,
  listOrgMembers,
  updateOrgMemberRole,
  removeOrgMember,
  inviteOrgOwner,
  type OrganizationDto,
  type WorkspaceDto,
  type MemberDto,
  type InviteDto,
  type ShareableLinkDto,
  type InvitableRole,
  type WorkspaceRole,
  type QuotaListDto,
  type OrgMemberDto,
  type UpdateQuotaItem,
} from '@/lib/settings';
import {
  getSessions,
  revokeSession,
  revokeAllSessions,
  signOut,
  signOutEverywhere,
  switchWorkspace,
  type SessionDto,
  type AuthResponse,
} from '@/lib/auth';
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
  type ApiKeyDto,
  type CreateApiKeyInput,
  type CreatedApiKeyDto,
} from '@/lib/api-keys';
import {
  bookPartnerCalendlySlot,
  deletePartnerIntegration,
  getPartnerAccessRequest,
  getPartnerAvailableSlots,
  getPartnerIntegration,
  notifyPartnerAccessRequest,
  registerPartnerIntegration,
  requestPartnerRemovalCode,
  submitPartnerAccessRequest,
  verifyPartnerIntegration,
  type NotifyPartnerAccessRequestInput,
  type PartnerAccessRequestStatus,
  type PartnerCalendlyBookedResponse,
  type PartnerCalendlyBookingInput,
  type PartnerIntegrationDto,
  type PartnerRemovalCodeResult,
  type RegisterPartnerIntegrationInput,
  type RemovePartnerIntegrationResult,
  type SubmitPartnerAccessRequestInput,
} from '@/lib/partner-integration';
import type { AvailableSlot } from '@/features/onboarding/schemas';
import { emailAccountKeys } from '@/features/email-accounts/queries';
import { useAuthStore } from '@/lib/stores/auth';
import { notifySuccess } from '@/lib/toast';
import { optimisticListUpdate, optimisticObjectPatch } from '@/lib/query-optimistic';
import { resourceScopedRedirectTarget } from '@/lib/route-guards';

/**
 * Hierarchical query keys per settings sub-domain. Invalidate at the right
 * level: `profileKeys.all` after profile mutations, `workspaceKeys.members(id)`
 * after member mutations, etc.
 */
export const profileKeys = {
  all: ['profile'] as const,
  current: () => [...profileKeys.all, 'current'] as const,
  warmupFilterId: () => [...profileKeys.all, 'warmup-filter-id'] as const,
};

export const orgKeys = {
  all: ['organization'] as const,
  current: () => [...orgKeys.all, 'current'] as const,
  members: () => [...orgKeys.all, 'members'] as const,
};

export const workspaceKeys = {
  all: ['workspaces'] as const,
  lists: () => [...workspaceKeys.all, 'list'] as const,
  list: () => [...workspaceKeys.lists()] as const,
  members: (wsId: number | null | undefined) =>
    [...workspaceKeys.all, 'members', wsId] as const,
  invites: (wsId: number | null | undefined) =>
    [...workspaceKeys.all, 'invites', wsId] as const,
  shareableLinks: (wsId: number | null | undefined) =>
    [...workspaceKeys.all, 'shareableLinks', wsId] as const,
  quotas: () => [...workspaceKeys.all, 'quotas'] as const,
};

export const sessionKeys = {
  all: ['sessions'] as const,
  list: () => [...sessionKeys.all, 'list'] as const,
};

export const apiKeyKeys = {
  all: ['api-keys'] as const,
  lists: () => [...apiKeyKeys.all, 'list'] as const,
};

export const partnerIntegrationKeys = {
  all: ['partner-integration'] as const,
  current: () => [...partnerIntegrationKeys.all, 'current'] as const,
  remove: () => [...partnerIntegrationKeys.all, 'remove'] as const,
  /** The access REQUEST — the step before a callback exists. */
  accessRequest: () => [...partnerIntegrationKeys.all, 'access-request'] as const,
  /** Slots for the partner onboarding-call event — a DIFFERENT Calendly event
   * than onboarding's own `onboardingKeys.slots`. */
  accessRequestSlots: (startTime?: string) =>
    [...partnerIntegrationKeys.accessRequest(), 'slots', startTime ?? 'default'] as const,
};

// ── Profile ────────────────────────────────────────────────────────────────

/** The org's Warmup Filter ID never changes for the org's lifetime — no refetching. */
export function useWarmupFilterIdQuery() {
  return useQuery({
    queryKey: profileKeys.warmupFilterId(),
    queryFn: getWarmupFilterId,
    staleTime: Infinity,
  });
}

export function useProfileQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: profileKeys.current(),
    queryFn: getProfile,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Derive role gating from the cached profile (`profile.role`, the API's source
 * of truth). One ladder now: `owner` is company-wide, the rest are
 * per-workspace, so both flags read the same field.
 *
 *   - `isOrgOwner`  → owner only (org timezone editor, workspace lifecycle,
 *                     billing).
 *   - `isOrgAdmin`  → owner OR admin (may assign/invite the Admin role).
 *
 * Client-side only — every one of these is server-enforced too.
 */
export function useOrgRole(): {
  isOrgOwner: boolean;
  isOrgAdmin: boolean;
  /**
   * Whether the profile has actually come back. Both booleans above are `false`
   * while it hasn't, which is indistinguishable from a resolved member — fine
   * for hiding a CTA (the safe direction), wrong for any branch that TELLS the
   * user what they are. `BlurLock` needs the distinction: showing an owner
   * "ask your workspace owner" because their profile hadn't loaded yet is a
   * confidently wrong statement, so it only branches once this is `true`.
   */
  isResolved: boolean;
} {
  const query = useProfileQuery();
  const profile = query.data ?? null;
  const isOrgOwner = profile?.role === 'owner';
  const isOrgAdmin = profile?.role === 'owner' || profile?.role === 'admin';
  return { isOrgOwner, isOrgAdmin, isResolved: query.isSuccess };
}

export function useUpdateProfileMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Profile updated' },
    mutationFn: (input: UpdateProfileInput) => updateProfile(input),
    // Optimistic on the cached profile so the form reflects immediately.
    ...optimisticObjectPatch<ProfileDto, UpdateProfileInput>(
      qc,
      profileKeys.current(),
      (previous, input) => ({
        ...previous,
        ...(input.name !== undefined ? { name: input.name } : {}),
        ...(input.timezone !== undefined ? { timezone: input.timezone } : {}),
        ...(input.theme !== undefined ? { theme: input.theme } : {}),
      }),
    ),
  });
}

// Not optimistic: the server assigns the avatar URL.
export function useUploadAvatarMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Photo updated' },
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: (next) => {
      qc.setQueryData(profileKeys.current(), next);
    },
  });
}

// ── Organization ───────────────────────────────────────────────────────────

export function useOrganizationQuery() {
  return useQuery<OrganizationDto>({
    queryKey: orgKeys.current(),
    queryFn: getOrganization,
  });
}

/**
 * Owner-only on the backend (`@RequiresOrgRole(OrgRole.OWNER)`); non-owners get
 * a 403. We optimistically patch `org.timezone` so the picker reflects the new
 * value immediately, then roll back on failure.
 */
export function useUpdateOrganizationTimezoneMutation() {
  const qc = useQueryClient();
  const patch = optimisticObjectPatch<OrganizationDto, string>(
    qc,
    orgKeys.current(),
    (previous, timezone) => ({ ...previous, timezone }),
  );
  return useMutation({
    // Errors surface via the section's page-level banner (`onError`), so
    // suppress the global error toast; success still toasts centrally.
    meta: { ...SILENT, successMessage: 'Timezone updated' },
    mutationFn: (timezone: string) => updateOrganizationTimezone(timezone),
    ...patch,
    onSuccess: (next: OrganizationDto) => {
      patch.onSuccess(next);
      // The account Overview and Activity tab date their events in the org
      // zone, so drop their cached copies rather than show the old zone for
      // up to `staleTime`.
      void qc.invalidateQueries({ queryKey: emailAccountKeys.all });
    },
  });
}

// ── Organization members (owner-only) ──────────────────────────────────────

/**
 * Owner-only on the backend (`GET /settings/organization/members`); non-owners
 * get a 403 — the query client's default retry already treats 403 as
 * non-retryable, so no override needed.
 */
export function useOrgMembersQuery() {
  return useQuery<OrgMemberDto[]>({
    queryKey: orgKeys.members(),
    queryFn: listOrgMembers,
  });
}

/**
 * Org-member role mutations touch BOTH the org-members list and the workspace
 * member lists (an owner row is locked in every workspace's Workspace access
 * list, and a demote rewrites the member's workspace memberships server-side). We refetch
 * the org-members key and every cached `workspaceKeys.members(...)` list rather
 * than patch — the server is the authority on the resulting membership set.
 */
function useOrgMemberRefetch() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: orgKeys.members() });
    // Prefix-invalidate every per-workspace member list.
    void qc.invalidateQueries({ queryKey: [...workspaceKeys.all, 'members'] });
  };
}

/**
 * Members screen → "Invite by email". Always an owner invite, so the payload is
 * just the address. `meta: SILENT` because the success copy names the invitee (so
 * it can't be a static `successMessage`) and the card owns its inline errors.
 * Prefix-invalidates every invite list: the server picks the anchor workspace, so
 * this hook can't name the one the row landed in.
 */
export function useInviteOrgOwnerMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: SILENT,
    mutationFn: (email: string) => inviteOrgOwner(email),
    onSuccess: (_invite, email) => {
      void qc.invalidateQueries({ queryKey: [...workspaceKeys.all, 'invites'] });
      notifySuccess(`Invitation sent to ${email}`);
    },
  });
}

/** A pending owner invite plus the workspace its row is anchored to — the
 * Workspace access tab's resend/revoke routes it reuses are workspace-scoped. */
export type PendingOwnerInvite = InviteDto & { workspaceId: number };

/**
 * Owner invites for the Members screen, via the Workspace access tab's per-workspace invite
 * list (an owner invite can sit in any workspace), keeping only `role === 'owner'`.
 */
export function usePendingOwnerInvitesQuery(workspaceIds: number[]) {
  return useQueries({
    queries: workspaceIds.map((wsId) => ({
      queryKey: workspaceKeys.invites(wsId),
      queryFn: () => listPendingInvites(wsId),
    })),
    combine: (results) => ({
      invites: results.flatMap((result, idx): PendingOwnerInvite[] =>
        (result.data ?? [])
          .filter((invite) => invite.role === 'owner')
          .map((invite) => ({ ...invite, workspaceId: workspaceIds[idx]! })),
      ),
      isPending: results.some((result) => result.isPending),
    }),
  });
}

export function usePromoteOwnerMutation() {
  const refetch = useOrgMemberRefetch();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Member promoted to owner' },
    mutationFn: (userId: number) => updateOrgMemberRole(userId, { role: 'owner' }),
    onSuccess: refetch,
  });
}

type DemoteOwnerPayload = { userId: number; workspaceId: number };

export function useDemoteOwnerMutation() {
  const refetch = useOrgMemberRefetch();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Owner demoted' },
    mutationFn: ({ userId, workspaceId }: DemoteOwnerPayload) =>
      updateOrgMemberRole(userId, { role: 'admin', workspaceId }),
    onSuccess: refetch,
  });
}

export function useRemoveOrgMemberMutation() {
  const refetch = useOrgMemberRefetch();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Member removed from organization' },
    mutationFn: (userId: number) => removeOrgMember(userId),
    onSuccess: refetch,
  });
}

// ── Workspaces ─────────────────────────────────────────────────────────────

export function useWorkspacesQuery() {
  return useQuery<WorkspaceDto[]>({
    queryKey: workspaceKeys.list(),
    queryFn: getWorkspaces,
  });
}

export function useMembersQuery(workspaceId: number | null | undefined) {
  return useQuery<MemberDto[]>({
    queryKey: workspaceKeys.members(workspaceId),
    queryFn: () => {
      if (workspaceId == null) {
        return Promise.resolve([] as MemberDto[]);
      }
      return listMembers(workspaceId);
    },
    enabled: workspaceId != null,
  });
}

/** Workspace access tab pending invites. Owner invites are dropped via `select` (per observer,
 * so the shared cache stays complete for the Members screen). */
export function usePendingInvitesQuery(workspaceId: number | null | undefined) {
  return useQuery<InviteDto[]>({
    queryKey: workspaceKeys.invites(workspaceId),
    queryFn: () => {
      if (workspaceId == null) {
        return Promise.resolve([] as InviteDto[]);
      }
      return listPendingInvites(workspaceId);
    },
    select: (invites) => invites.filter((invite) => invite.role !== 'owner'),
    enabled: !!workspaceId,
  });
}

export function useListShareableLinksQuery(workspaceId: number | null | undefined) {
  return useQuery<ShareableLinkDto[]>({
    queryKey: workspaceKeys.shareableLinks(workspaceId),
    queryFn: () => {
      if (workspaceId == null) {
        return Promise.resolve([] as ShareableLinkDto[]);
      }
      return listShareableLinks(workspaceId);
    },
    enabled: workspaceId != null,
  });
}

/**
 * Quotas: 403 for non-owners is expected. We surface success/empty via the
 * `data` and `error` slots — callers can check `isError` and degrade. The
 * query client's default retry already treats 403 as non-retryable.
 */
export function useQuotasQuery() {
  return useQuery<QuotaListDto>({
    queryKey: workspaceKeys.quotas(),
    queryFn: getQuotas,
  });
}

type CreateWorkspacePayload = { name: string; dailyWarmupBudget?: number };

export function useCreateWorkspaceMutation() {
  const qc = useQueryClient();
  return useMutation({
    // Dynamic message (workspace name) — toast inline, don't set successMessage.
    meta: SILENT,
    mutationFn: ({ name, dailyWarmupBudget }: CreateWorkspacePayload) =>
      createWorkspace(name, dailyWarmupBudget),
    onSuccess: (ws) => {
      qc.setQueryData<WorkspaceDto[]>(workspaceKeys.list(), (prev) =>
        prev ? [...prev, ws] : [ws],
      );
      void qc.invalidateQueries({ queryKey: workspaceKeys.quotas() });
      notifySuccess(`Workspace "${ws.name}" created`);
    },
  });
}

type UpdateWorkspacePayload = { wsId: number; name: string };

export function useUpdateWorkspaceMutation() {
  const qc = useQueryClient();
  return useMutation({
    // Dynamic message (workspace name) + the section owns inline error UI, so
    // stay silent on error and toast success inline.
    meta: SILENT,
    mutationFn: ({ wsId, name }: UpdateWorkspacePayload) => updateWorkspace(wsId, name),
    onSuccess: (ws) => {
      qc.setQueryData<WorkspaceDto[]>(workspaceKeys.list(), (prev) =>
        prev ? prev.map((w) => (w.id === ws.id ? { ...w, name: ws.name } : w)) : prev,
      );
      // If the active workspace was renamed, patch the auth store so the new
      // name shows everywhere immediately (sidebar badge, switcher, header).
      const current = useAuthStore.getState().workspace;
      if (current && current.id === ws.id) {
        useAuthStore.setState({ workspace: { ...current, name: ws.name } });
      }
      notifySuccess(`Workspace renamed to "${ws.name}"`);
    },
  });
}

export function useDeleteWorkspaceMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Workspace deleted' },
    mutationFn: (wsId: number) => deleteWorkspace(wsId),
    onSuccess: (_void, wsId) => {
      qc.setQueryData<WorkspaceDto[]>(workspaceKeys.list(), (prev) =>
        prev ? prev.filter((w) => w.id !== wsId) : prev,
      );
      // Members/invites for the deleted workspace are now stale.
      void qc.invalidateQueries({ queryKey: workspaceKeys.members(wsId) });
      void qc.invalidateQueries({ queryKey: workspaceKeys.invites(wsId) });
      // The quota allocation list (Workspaces tab's inline quota editors)
      // still has the deleted workspace's row cached — the backend cascade
      // tears down its quota row too, so refetch rather than hand-patch the
      // cached list.
      void qc.invalidateQueries({ queryKey: workspaceKeys.quotas() });
    },
  });
}

type InviteMemberPayload = {
  workspaceId: number;
  email: string;
  role: InvitableRole;
};

export function useInviteMemberMutation() {
  const qc = useQueryClient();
  return useMutation({
    // Dynamic message (invitee email) — toast inline, don't set successMessage.
    meta: SILENT,
    mutationFn: ({ workspaceId, email, role }: InviteMemberPayload) =>
      inviteMember(workspaceId, email, role),
    onSuccess: (_invite, { workspaceId, email }) => {
      void qc.invalidateQueries({ queryKey: workspaceKeys.invites(workspaceId) });
      notifySuccess(`Invitation sent to ${email}`);
    },
  });
}

type InviteIdPayload = { workspaceId: number; inviteId: number };

export function useResendInviteMutation() {
  return useMutation({
    meta: { ...SILENT, successMessage: 'Invitation resent' },
    mutationFn: ({ workspaceId, inviteId }: InviteIdPayload) =>
      resendInvite(workspaceId, inviteId),
  });
}

export function useRevokeInviteMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Invitation revoked' },
    mutationFn: ({ workspaceId, inviteId }: InviteIdPayload) =>
      revokeInvite(workspaceId, inviteId),
    ...optimisticListUpdate<InviteDto, InviteIdPayload>(
      qc,
      ({ workspaceId }) => workspaceKeys.invites(workspaceId),
      (list, { inviteId }) => list.filter((i) => i.id !== inviteId),
    ),
  });
}

type MemberIdPayload = { workspaceId: number; userId: number };

export function useRemoveMemberMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Member removed' },
    mutationFn: ({ workspaceId, userId }: MemberIdPayload) =>
      removeMember(workspaceId, userId),
    ...optimisticListUpdate<MemberDto, MemberIdPayload>(
      qc,
      ({ workspaceId }) => workspaceKeys.members(workspaceId),
      (list, { userId }) => list.filter((m) => m.userId !== userId),
    ),
    // This mutation can strip a user's last workspace-ADMIN row in the org,
    // which the backend then downgrades to org MEMBER as a side effect (see
    // stale-org-role-after-admin-demotion.md). Invalidate the org-members
    // cache unconditionally so the Organization Members tab never shows a
    // stale "Admin" chip — same blanket-invalidate philosophy as
    // useOrgMemberRefetch above.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.members() });
    },
  });
}

type UpdateMemberRolePayload = MemberIdPayload & { role: WorkspaceRole };

export function useUpdateMemberRoleMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Role updated' },
    mutationFn: ({ workspaceId, userId, role }: UpdateMemberRolePayload) =>
      updateMemberRole(workspaceId, userId, role),
    ...optimisticListUpdate<MemberDto, UpdateMemberRolePayload>(
      qc,
      ({ workspaceId }) => workspaceKeys.members(workspaceId),
      (list, { userId, role }) =>
        list.map((m) => (m.userId === userId ? { ...m, role } : m)),
    ),
    // This mutation can strip a user's last workspace-ADMIN row in the org,
    // which the backend then downgrades to org MEMBER as a side effect (see
    // stale-org-role-after-admin-demotion.md). Invalidate the org-members
    // cache unconditionally so the Organization Members tab never shows a
    // stale "Admin" chip — same blanket-invalidate philosophy as
    // useOrgMemberRefetch above.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKeys.members() });
    },
  });
}

type AddMemberToWorkspacePayload = {
  workspaceId: number;
  userId: number;
  role: InvitableRole;
};

/**
 * People section → "Add to a workspace". Same refetch shape as
 * `useOrgMemberRefetch` (invalidate the org-members roster + every cached
 * per-workspace member list) rather than a hand-patch — the new row changes
 * both the target workspace's member list AND the person's `workspaces[]`
 * entry on the org roster, and the server is the authority on both.
 */
export function useAddMemberToWorkspaceMutation() {
  const refetch = useOrgMemberRefetch();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Added to workspace' },
    mutationFn: ({ workspaceId, userId, role }: AddMemberToWorkspacePayload) =>
      addMemberToWorkspace(workspaceId, userId, role),
    onSuccess: refetch,
  });
}

type CreateShareableLinkPayload = {
  workspaceId: number;
  role: InvitableRole;
  expiresInDays?: number;
};


export function useCreateShareableLinkMutation() {
  const qc = useQueryClient();
  return useMutation<ShareableLinkDto, Error, CreateShareableLinkPayload>({
    meta: SILENT,
    mutationFn: ({ workspaceId, role }) => createShareableLink(workspaceId, role),
    onSuccess: (_link, { workspaceId }) => {
      void qc.invalidateQueries({
        queryKey: workspaceKeys.shareableLinks(workspaceId),
      });
    },
  });
}

type RevokeShareableLinkPayload = { workspaceId: number; linkId: number };

export function useRevokeShareableLinkMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, RevokeShareableLinkPayload>({
    meta: SILENT,
    mutationFn: ({ workspaceId, linkId }) =>
      revokeShareableLink(workspaceId, linkId),
    onSuccess: (_void, { workspaceId }) => {
      void qc.invalidateQueries({
        queryKey: workspaceKeys.shareableLinks(workspaceId),
      });
    },
  });
}

type UpdateQuotaPayload = { quotas: UpdateQuotaItem[] };

/**
 * Batch update — the caller passes only the workspaces it actually changed
 * (`quotas`), not every workspace in the org. The response is one `QuotaDto`
 * per requested workspace.
 */
export function useUpdateQuotaMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Warmup budgets updated' },
    mutationFn: ({ quotas }: UpdateQuotaPayload) => updateQuota(quotas),
    onSuccess: (updatedList) => {
      const updatedById = new Map(updatedList.map((q) => [q.workspaceId, q]));
      // Patch the quota list. `allocated` is recomputed from the patched rows
      // (never incremented by a delta) so the dialog's headroom line stays
      // consistent with the rows it sits above without waiting for a refetch.
      // `orgLimit` rides along untouched via the spread — a PATCH can't change
      // the plan cap.
      qc.setQueryData<QuotaListDto>(workspaceKeys.quotas(), (prev) => {
        if (!prev) return prev;
        const workspaces = prev.workspaces.map((q) => {
          const updated = updatedById.get(q.workspaceId);
          return updated ? { ...q, dailyWarmupBudget: updated.dailyWarmupBudget } : q;
        });
        return {
          ...prev,
          workspaces,
          allocated: workspaces.reduce((sum, q) => sum + q.dailyWarmupBudget, 0),
        };
      });
      // …and the workspace list, which carries a `quota` field.
      qc.setQueryData<WorkspaceDto[]>(workspaceKeys.list(), (prev) =>
        prev
          ? prev.map((w) => {
              const updated = updatedById.get(w.id);
              return updated
                ? { ...w, quota: { dailyWarmupBudget: updated.dailyWarmupBudget } }
                : w;
            })
          : prev,
      );
    },
  });
}

// ── Sessions / auth ────────────────────────────────────────────────────────

export function useSessionsQuery() {
  return useQuery<SessionDto[]>({
    queryKey: sessionKeys.list(),
    queryFn: getSessions,
  });
}

export function useRevokeSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Session revoked' },
    mutationFn: (id: number) => revokeSession(id),
    ...optimisticListUpdate<SessionDto, number>(
      qc,
      () => sessionKeys.list(),
      (list, id) => list.filter((s) => s.id !== id),
    ),
  });
}

export function useRevokeAllSessionsMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'Signed out of all other sessions' },
    mutationFn: revokeAllSessions,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionKeys.list() });
    },
  });
}

/**
 * Sign-out is cross-cutting: it tears down the whole local cache because the
 * next user (if any) must not see stale tenant data.
 */
export function useSignOutMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: SILENT,
    mutationFn: signOut,
    onSettled: () => {
      qc.clear();
    },
  });
}

/**
 * "Sign out everywhere": revokes every other session, then this one. If the
 * revoke fails nothing local is torn down, so the caller can surface the error
 * and the user can retry.
 */
export function useSignOutEverywhereMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: SILENT,
    mutationFn: signOutEverywhere,
    onSuccess: () => {
      qc.clear();
    },
  });
}

// ── API keys ─────────────────────────────────────────────────────────────────

export function useApiKeysQuery() {
  return useQuery<ApiKeyDto[]>({
    queryKey: apiKeyKeys.lists(),
    queryFn: listApiKeys,
  });
}

// ── Partner integration ──────────────────────────────────────────────────────

/**
 * `null` data means "nothing registered yet", which the request layer maps from
 * the endpoint's 404. Not an error state, so no retry games are needed here.
 *
 * Pass `isOrgOwner` as `enabled` — every route on `PartnerIntegrationController`
 * is `@RequiresWorkspaceRole(OWNER)`, so a non-owner's request is a guaranteed 403.
 */
export function usePartnerIntegrationQuery(enabled = true) {
  return useQuery<PartnerIntegrationDto | null>({
    queryKey: partnerIntegrationKeys.current(),
    queryFn: getPartnerIntegration,
    enabled,
  });
}

/**
 * Whether this org has already filed a partner-access request.
 *
 * Enabled for owners REGARDLESS of `partnerIntegrationAccess` — that is the
 * point: it is what the card reads to choose between the request form and the
 * "already submitted" note, and an org with the grant never renders either.
 */
export function usePartnerAccessRequestQuery(enabled = true) {
  return useQuery<PartnerAccessRequestStatus>({
    queryKey: partnerIntegrationKeys.accessRequest(),
    queryFn: getPartnerAccessRequest,
    enabled,
  });
}

/**
 * File the request. `meta: SILENT` because the card owns its inline error copy;
 * the one interesting failure (`150301`, already submitted) is better answered
 * by re-rendering the submitted state than by a toast.
 */
export function useSubmitPartnerAccessRequestMutation() {
  const qc = useQueryClient();
  return useMutation<PartnerAccessRequestStatus, Error, SubmitPartnerAccessRequestInput>({
    meta: SILENT,
    mutationFn: submitPartnerAccessRequest,
    onSuccess: (data) => {
      qc.setQueryData(partnerIntegrationKeys.accessRequest(), data);
    },
  });
}

/** Forward the filed request to our team once the booking step resolves. `meta: SILENT`
 * and no success copy: an internal hand-off whose failure the user can't act on. */
export function useNotifyPartnerAccessRequestMutation() {
  return useMutation<void, Error, NotifyPartnerAccessRequestInput>({
    meta: SILENT,
    mutationFn: notifyPartnerAccessRequest,
  });
}

/** Slots for the partner onboarding-call Calendly event: the partner twin of
 * `useAvailableSlotsQuery`, from `/partner/integration/access-request/available-slots`. */
export function usePartnerAvailableSlotsQuery(startTime?: string, enabled = true) {
  return useQuery<AvailableSlot[]>({
    queryKey: partnerIntegrationKeys.accessRequestSlots(startTime),
    queryFn: () => getPartnerAvailableSlots(startTime),
    enabled,
  });
}

/** Books the mandatory call for the org's filed request: the partner twin of
 * `useRecordCalendlyBookingMutation`. `meta: silentError` because `CalendlyStep`
 * shows its own inline `AlertBanner`, so a toast would double up. */
export function useBookPartnerCalendlySlotMutation() {
  return useMutation<PartnerCalendlyBookedResponse, Error, PartnerCalendlyBookingInput>({
    meta: { silentError: true },
    mutationFn: bookPartnerCalendlySlot,
  });
}

/**
 * Register or rotate. `meta: SILENT` because the form owns its own inline error
 * copy — a failed verification needs the specific "is your endpoint live?"
 * prompt, not a generic banner, and that is the overwhelmingly common failure.
 */
export function useRegisterPartnerIntegrationMutation() {
  const qc = useQueryClient();
  return useMutation<PartnerIntegrationDto, Error, RegisterPartnerIntegrationInput>({
    meta: SILENT,
    mutationFn: registerPartnerIntegration,
    onSuccess: (data) => {
      qc.setQueryData(partnerIntegrationKeys.current(), data);
    },
  });
}

/** Re-verify the stored pair. Toasts, because there is no other visible result. */
export function useVerifyPartnerIntegrationMutation() {
  const qc = useQueryClient();
  return useMutation<PartnerIntegrationDto, Error, void>({
    meta: { successMessage: 'Callback endpoint verified' },
    mutationFn: verifyPartnerIntegration,
    onSuccess: (data) => {
      qc.setQueryData(partnerIntegrationKeys.current(), data);
    },
  });
}

/**
 * Whether a removal is in flight anywhere — the mutation lives in the removal
 * dialog, so the card reads it by key rather than holding a second instance
 * whose `isPending` would never flip.
 */
export function useIsRemovingPartnerIntegration(): boolean {
  return useIsMutating({ mutationKey: partnerIntegrationKeys.remove() }) > 0;
}

/**
 * Email the removal confirmation code to every owner. `meta: SILENT` — the
 * removal dialog owns the error copy (cooldown, delivery failure) inline.
 */
export function useRequestPartnerRemovalCodeMutation() {
  return useMutation<PartnerRemovalCodeResult, Error, void>({
    meta: SILENT,
    mutationFn: requestPartnerRemovalCode,
  });
}

/**
 * Removal deletes every mailbox linked to the integration, so the toast names
 * the count (inline `notifySuccess`, not `meta.successMessage` — the message is
 * interpolated) and the email-account caches are invalidated: a list already in
 * the cache would still show the deleted rows.
 *
 * Takes the emailed confirmation code. `meta: SILENT` because a wrong code is
 * shown next to the code field, not as a toast over the dialog.
 */
export function useDeletePartnerIntegrationMutation() {
  const qc = useQueryClient();
  return useMutation<RemovePartnerIntegrationResult, Error, string>({
    mutationKey: partnerIntegrationKeys.remove(),
    meta: SILENT,
    mutationFn: deletePartnerIntegration,
    onSuccess: ({ deletedAccounts }) => {
      qc.setQueryData(partnerIntegrationKeys.current(), null);
      if (deletedAccounts > 0) {
        void qc.invalidateQueries({ queryKey: emailAccountKeys.all });
      }
      notifySuccess(
        deletedAccounts === 0
          ? 'Partner integration removed'
          : `Partner integration removed. ${deletedAccounts} ${deletedAccounts === 1 ? 'mailbox was' : 'mailboxes were'} deleted.`,
      );
    },
  });
}

/**
 * Not optimistic — the server assigns the id and the one-time plaintext key.
 * Stays `meta: SILENT` (no success message): the dialog owns inline error UI
 * (branching on errorCode) and the one-time reveal step IS the feedback.
 */
export function useCreateApiKeyMutation() {
  const qc = useQueryClient();
  return useMutation<CreatedApiKeyDto, Error, CreateApiKeyInput>({
    meta: SILENT,
    mutationFn: createApiKey,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: apiKeyKeys.lists() });
    },
  });
}

/**
 * Revoke optimistically patches the matching row's `revokedAt` to a timestamp
 * (the row stays in the list, rendered muted) rather than removing it.
 */
export function useRevokeApiKeyMutation() {
  const qc = useQueryClient();
  return useMutation({
    meta: { ...SILENT, successMessage: 'API key revoked' },
    mutationFn: revokeApiKey,
    ...optimisticListUpdate<ApiKeyDto, number>(
      qc,
      () => apiKeyKeys.lists(),
      (list, id) =>
        list.map((k) =>
          k.id === id ? { ...k, revokedAt: new Date().toISOString() } : k,
        ),
    ),
  });
}


export function useSwitchWorkspaceMutation() {
  const qc = useQueryClient();
  // `useRouter()` (not `useNavigate()`/`useRouterState()`) so `onSuccess`
  // reads `router.state.location.pathname` live, at the moment it fires,
  // rather than a render-time value captured in a stale closure — and so
  // this hook doesn't re-render on every pathname change just to stay current.
  const router = useRouter();
  return useMutation<AuthResponse, Error, number>({
    meta: { ...SILENT, successMessage: 'Switched workspace' },
    mutationFn: (id: number) => switchWorkspace(id),
    onSuccess: async (result) => {
      // Rotate the token/tenant FIRST. The new workspace lives in the JWT and
      // `lib/api.ts` reads the bearer from storage at request time, so the
      // store write (which the persist adapter flushes to the `access_token`
      // key synchronously) must land before we kick off any refetch — invalidate
      // before this and the refetches below go out under the OLD workspace and
      // re-poison the cache with the previous tenant's data.
      useAuthStore.getState().setAuth(result);
      // If we're on a resource-scoped route (e.g. `/email-accounts/$id`), its
      // id only exists in the workspace we just left. Await the navigate
      // BEFORE invalidating below, so the old route unmounts first — otherwise
      // its still-mounted query gets refetched under the new tenant and 404s.
      const redirectTo = resourceScopedRedirectTarget(router.state.location.pathname);
      if (redirectTo) {
        await router.navigate({ to: redirectTo, replace: true });
      }
      // Switching workspace changes the tenant for EVERY server resource. A
      // hand-picked allowlist (workspaces/profile/sessions/org/emailAccounts)
      // silently leaks the previous workspace's data on any other surface
      // (dashboard, billing, and any future feature key). Invalidate the whole
      // cache with no filter: this marks every query stale and refetches the
      // active ones (with the new token), so the current screen reloads under
      // the new workspace. Unlike sign-out's qc.clear(): the user stays in-app,
      // so we invalidate-in-place to avoid an empty-skeleton flash.
      void qc.invalidateQueries();
    },
  });
}
